import { useState } from 'react';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Building2 } from 'lucide-react';
import type { ActivationLookup, AuthUser } from '@runik/shared';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Spinner } from '../components/ui/spinner';
import { ApiError, request } from '../lib/auth/client';
import { meQueryKey } from '../lib/auth/hooks';

const activationFormSchema = z
  .object({
    password: z.string().min(12, 'En az 12 karakter').max(256),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Parolalar eşleşmiyor',
  });
type ActivationFormInput = z.infer<typeof activationFormSchema>;

function ActivatePage() {
  const { token } = useParams({ from: '/activate/$token' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  const lookup = useQuery({
    queryKey: ['activation', token],
    queryFn: () => request<ActivationLookup>(`/api/activation/${token}`),
    retry: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ActivationFormInput>({ resolver: zodResolver(activationFormSchema) });

  const onSubmit = async (data: ActivationFormInput) => {
    setServerError(null);
    try {
      const result = await request<{ user: AuthUser }>(`/api/activation/${token}`, {
        method: 'POST',
        body: JSON.stringify({ password: data.password }),
      });
      qc.setQueryData(meQueryKey, result.user);
      await navigate({ to: '/dashboard' });
    } catch (err) {
      setServerError(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Bir hata oluştu');
    }
  };

  if (lookup.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (!lookup.data?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 text-rose-600">
              <AlertCircle className="h-5 w-5" />
              <CardTitle>Geçersiz Link</CardTitle>
            </div>
            <CardDescription>
              Bu davet linki geçersiz, süresi dolmuş veya zaten kullanılmış. Yeni bir davet için
              yöneticinizle iletişime geçin.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2 text-brand">
            <Building2 className="h-5 w-5" />
            <CardTitle>Hesabınızı Aktive Edin</CardTitle>
          </div>
          <CardDescription>
            <strong>{lookup.data.customer?.companyName}</strong> için yeni bir parola belirleyin.
            Aktivasyon e-postası: {lookup.data.customer?.billingEmail}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            {serverError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="password">Parola</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                autoFocus
                {...register('password')}
              />
              {errors.password && <p className="text-sm text-rose-600">{errors.password.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Parola (tekrar)</Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-rose-600">{errors.confirmPassword.message}</p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? <><Spinner /> Aktive ediliyor…</> : 'Aktive Et ve Giriş Yap'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/activate/$token')({ component: ActivatePage });
